// src/components/Header.jsx
import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { withTheme } from "../theming";
import { userPool } from "../awsConfig";
import { useResponsive } from "../contexts/ResponsiveContext";
import { clearAuthState } from "../utils/authUtils";

const Header = ({ isAuthenticated, setIsAuthenticated, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isPhoneLandscape } = useResponsive();
  const showHamburger = isMobile || isPhoneLandscape;

  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/confirm-signup";

  const shouldShowMenuItems = isAuthenticated && !isAuthPage;

  const handleLogout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    setIsOpen(false);
    clearAuthState();
    setIsAuthenticated(false);
    window.location.replace("/login");
  };

  const handleMenuItemClick = () => setIsOpen(false);

  // Public nav — visible to all visitors
  const publicLinks = [
    { name: "Gallery", path: "/gallery" },
    { name: "Shop",    path: "/store" },
    { name: "About",   path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  // Admin nav — visible only when artist is logged in
  const adminLinks = [
    { name: "Dashboard",       path: "/dashboard" },
    { name: "Upload Art",      path: "/upload" },
    { name: "Manage Gallery",  path: "/manage-gallery" },
    { name: "Collections",     path: "/manage-collections" },
    { name: "Manage Store",    path: "/manage-store" },
    { name: "Blogs",           path: "/blogs" },
    { name: "Manage Bio",      path: "/edit-about" },
    { name: "Your Brand",      path: "/brand-studio" },
  ];

  const navLinks = shouldShowMenuItems ? adminLinks : publicLinks;

  return (
    <header
      style={{
        backgroundColor: theme.backgroundColor || "#333",
        color: theme.textColor || "#FFF",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
      className="p-4"
    >
      <div className="mx-auto flex justify-between items-center pr-10 pl-5">
        {/* Logo */}
        <div className="flex-shrink-0 mr-8">
          <NavLink
            to="/"
            className={({ isActive }) =>
              isActive
                ? "font-bold text-[170%] hover:opacity-90 focus-visible:opacity-90"
                : "text-[170%] font-bold hover:opacity-90 focus:opacity-90"
            }
            style={{ color: theme.accentColor || "#5C7A5E" }}
          >
            Peaceful Oasis
          </NavLink>
        </div>

        {/* Hamburger icon — mobile only */}
        <div className={showHamburger ? "block" : "hidden"}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            className="focus:outline-none"
            style={{
              backgroundColor: theme.backgroundColor || "#333",
              color: theme.accentColor || "#5C7A5E",
            }}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        {/* Desktop nav */}
        <nav className={showHamburger ? "hidden" : "flex space-x-6"}>
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                isActive
                  ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                  : "text-[120%] hover:opacity-90 focus:opacity-90"
              }
              style={{ color: theme.accentColor || "#5C7A5E" }}
            >
              {link.name}
            </NavLink>
          ))}

          {shouldShowMenuItems ? (
            <NavLink
              to="#"
              onClick={handleLogout}
              className={({ isActive }) =>
                isActive
                  ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                  : "text-[120%] hover:opacity-90 focus:opacity-90"
              }
              style={{ color: theme.accentColor || "#5C7A5E" }}
            >
              Logout
            </NavLink>
          ) : (
            <NavLink
              to="/login"
              className={({ isActive }) =>
                isActive
                  ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                  : "text-[120%] hover:opacity-90 focus:opacity-90"
              }
              style={{ color: theme.accentColor || "#5C7A5E" }}
            >
              Login
            </NavLink>
          )}
        </nav>
      </div>

      {/* Mobile dropdown menu */}
      {isOpen && (
        <nav className={`${showHamburger ? "block" : "hidden"} mt-4 max-h-[calc(100vh-80px)] overflow-y-auto`}>
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              onClick={handleMenuItemClick}
              className="block py-2 px-4 text-[120%]"
              style={{ color: theme.accentColor || "#5C7A5E" }}
            >
              {link.name}
            </NavLink>
          ))}

          {shouldShowMenuItems ? (
            <NavLink
              to="#"
              onClick={handleLogout}
              className="block py-2 px-4 text-[120%]"
              style={{ color: theme.accentColor || "#5C7A5E" }}
            >
              Logout
            </NavLink>
          ) : (
            <NavLink
              to="/login"
              onClick={handleMenuItemClick}
              className="block py-2 px-4 text-[120%]"
              style={{ color: theme.accentColor || "#5C7A5E" }}
            >
              Login
            </NavLink>
          )}
        </nav>
      )}
    </header>
  );
};

export default withTheme(Header);
